/**
 * Theme context for managing light/dark/auto theme modes and theme switching.
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSettings } from './SettingsContext';
import { getTheme, getDefaultTheme, Theme } from '../themes/manager';
import { logWarning } from '../utils/errorLogging';

export type ThemeMode = 'light' | 'dark' | 'auto';

interface ThemeContextType {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  effectiveTheme: 'light' | 'dark'; // The actual theme being used (resolves 'auto')
  currentTheme: Theme; // The current theme (illinois, etc.)
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { settings } = useSettings();
  
  // Get theme from settings or default to 'professional'
  const themeId = settings?.color_scheme || 'professional';
  const [currentTheme, setCurrentTheme] = useState<Theme>(() => {
    return getTheme(themeId) || getDefaultTheme();
  });
  
  // Get initial theme mode from localStorage or default to 'auto'
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('theme') as ThemeMode;
    return saved && ['light', 'dark', 'auto'].includes(saved) ? saved : 'auto';
  });

  // Determine effective theme (resolves 'auto' to light or dark based on system preference)
  const getEffectiveTheme = (mode: ThemeMode): 'light' | 'dark' => {
    if (mode === 'auto') {
      // Check if matchMedia is available (for testing environments)
      if (typeof window !== 'undefined' && window.matchMedia) {
        try {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
          if (mediaQuery && typeof mediaQuery.matches === 'boolean') {
            return mediaQuery.matches ? 'dark' : 'light';
          }
        } catch (error) {
          // Fallback for test environments
        }
      }
      // Default to light if matchMedia is not available
      return 'light';
    }
    return mode;
  };

  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>(() => {
    try {
      return getEffectiveTheme(theme);
    } catch {
      // Fallback for testing environments
      return theme === 'dark' ? 'dark' : 'light';
    }
  });

  // Update theme in localStorage and apply to document
  const setTheme = (newTheme: ThemeMode) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
    const effective = getEffectiveTheme(newTheme);
    setEffectiveTheme(effective);
    applyTheme(effective);
  };

  // Apply theme to document root
  const applyTheme = (effective: 'light' | 'dark') => {
    const root = document.documentElement;
    if (effective === 'dark') {
      root.classList.add('dark-theme');
      root.classList.remove('light-theme');
    } else {
      root.classList.add('light-theme');
      root.classList.remove('dark-theme');
    }
  };

  // Listen for system theme changes when in 'auto' mode
  useEffect(() => {
    applyTheme(effectiveTheme);

    if (theme === 'auto' && typeof window !== 'undefined' && window.matchMedia) {
      try {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        if (!mediaQuery) return;
        
        const handleChange = () => {
          const effective = getEffectiveTheme('auto');
          setEffectiveTheme(effective);
          applyTheme(effective);
        };

        // Modern browsers
        if (typeof mediaQuery.addEventListener === 'function') {
          mediaQuery.addEventListener('change', handleChange);
          return () => {
            if (mediaQuery && typeof mediaQuery.removeEventListener === 'function') {
              mediaQuery.removeEventListener('change', handleChange);
            }
          };
        }
        // Fallback for older browsers
        else if (typeof mediaQuery.addListener === 'function') {
          mediaQuery.addListener(handleChange);
          return () => {
            if (mediaQuery && typeof mediaQuery.removeListener === 'function') {
              mediaQuery.removeListener(handleChange);
            }
          };
        }
      } catch (error) {
        // Silently fail in test environments
        logWarning('Failed to set up theme listener', error);
      }
    }
  }, [theme, effectiveTheme]);

  // Initialize theme immediately on mount (before settings load)
  useEffect(() => {
    // Set default theme immediately so CSS variables are available
    const defaultThemeId = 'professional';
    document.documentElement.setAttribute('data-theme', defaultThemeId);
    const effective = getEffectiveTheme(theme);
    setEffectiveTheme(effective);
    applyTheme(effective);
  }, []); // Run once on mount

  // Update theme when settings load or change
  useEffect(() => {
    const effective = getEffectiveTheme(theme);
    setEffectiveTheme(effective);
    applyTheme(effective);
    
    // Set theme data attribute (defaults to professional if no settings yet)
    const themeId = settings?.color_scheme || 'professional';
    const root = document.documentElement;
    root.setAttribute('data-theme', themeId);
  }, [settings?.color_scheme, theme]);

  // Update current theme when settings change
  useEffect(() => {
    const newThemeId = settings?.color_scheme || 'professional';
    const newTheme = getTheme(newThemeId) || getDefaultTheme();
    setCurrentTheme(newTheme);
    
    // Apply theme-specific data attribute to root element for CSS scoping
    // This ensures only the active theme's CSS variables are applied
    const root = document.documentElement;
    root.setAttribute('data-theme', newThemeId);
    
    // Also ensure light/dark theme class is set
    const effective = getEffectiveTheme(theme);
    applyTheme(effective);
  }, [settings?.color_scheme, theme]);

  const value: ThemeContextType = {
    theme,
    setTheme,
    effectiveTheme,
    currentTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

